/**
 * One serial queue in front of the transport.
 *
 * - Jobs run ONE AT A TIME, in the order they were queued. Copies are separate jobs, so N copies
 *   print one after another — v1 mobile opened N overlapping connections for them.
 * - At least `gapMs` separates a job from the one that finished before it, however the two were
 *   queued (thermal printers drop a job that arrives on the heels of another; Electron's main
 *   process waits 500 ms for the same reason). A queue idle for longer starts a job at once.
 * - Every job resolves exactly once with an Outcome: a transport that throws becomes
 *   `TRANSPORT_ERROR`, one that never answers becomes `TIMEOUT` — and the queue moves on either way.
 *
 * ponytail: one global queue, because React Native's printer libraries hold a single connection per
 * printer type. Key it per destination if throughput is ever measured as a problem.
 */
import type { Outcome, PrintJob, PrintTransport } from "./types";

export interface QueueOptions {
  gapMs: number;
  timeoutMs: number;
}

export interface PrintQueue {
  run(job: PrintJob): Promise<Outcome>;
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** `promise`, or `fallback` once `ms` pass — whichever is first. The timer never outlives the race. */
export function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<T>((resolve) => {
    timer = setTimeout(() => resolve(fallback), ms);
  });
  // guarded, not `clearTimeout(timer)`: React Native's typings take a number only
  return Promise.race([promise, timedOut]).finally(() => timer !== undefined && clearTimeout(timer));
}

function attempt(transport: PrintTransport, job: PrintJob, timeoutMs: number): Promise<Outcome> {
  let sent: Promise<Outcome>;
  try {
    sent = Promise.resolve(transport.send(job));
  } catch (error) {
    sent = Promise.reject(error);
  }
  const settled = sent.then(
    (outcome) => outcome ?? { ok: false, code: "TRANSPORT_ERROR", detail: "transport resolved nothing" },
    (error: any) => ({ ok: false, code: "TRANSPORT_ERROR", detail: String(error?.message ?? error) }) as Outcome,
  );
  return withTimeout(settled, timeoutMs, { ok: false, code: "TIMEOUT" } as Outcome);
}

export function createQueue(transport: PrintTransport, { gapMs, timeoutMs }: QueueOptions): PrintQueue {
  let tail: Promise<void> = Promise.resolve();
  // when the previous job finished — the gap is measured from THAT, so it holds between jobs from
  // separate requests too, not only between jobs that happened to be queued together
  let lastEnd = -Infinity;

  return {
    run(job) {
      let resolveOutcome!: (outcome: Outcome) => void;
      const outcome = new Promise<Outcome>((resolve) => (resolveOutcome = resolve));
      tail = tail.then(async () => {
        // 🔴 Nothing in here may reject. A rejected `tail` is a POISONED queue: every job after it
        // inherits the rejection, none of their `resolveOutcome`s is ever called, and the device
        // stops printing — silently, until the app is restarted. `attempt` already answers instead
        // of throwing, so this catch is a guard rather than a path; it settles the job it belongs
        // to and leaves the chain fulfilled for the next one.
        try {
          const rested = Date.now() - lastEnd;
          if (gapMs > 0 && rested < gapMs) await wait(gapMs - rested);
          resolveOutcome(await attempt(transport, job, timeoutMs));
        } catch (error: any) {
          resolveOutcome({ ok: false, code: "TRANSPORT_ERROR", detail: String(error?.message ?? error) });
        } finally {
          lastEnd = Date.now();
        }
      });
      return outcome;
    },
  };
}
